import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useHydrated from '../hooks/useHydrated';
import './GoldThread.css';

// Wide enough that the thread sits in the margin beside the 1180px panels.
const THREAD_QUERY = '(min-width: 1240px)';

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const prefersReducedMotion = () =>
  Boolean(window.matchMedia) && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A gold line in the left margin that draws itself as the page is read.
// Sections marked with data-canto become stops along it.
export default function GoldThread() {
  const hydrated = useHydrated();
  const { pathname } = useLocation();
  const threadRef = useRef(null);
  const stopsRef = useRef([]);
  const [enabled, setEnabled] = useState(false);
  const [stops, setStops] = useState([]);
  const [reached, setReached] = useState(0);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(THREAD_QUERY);
    const apply = () => setEnabled(query.matches);
    apply();
    if (query.addEventListener) {
      query.addEventListener('change', apply);
      return () => query.removeEventListener('change', apply);
    }
    query.addListener(apply);
    return () => query.removeListener(apply);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let frame = 0;

    const scrollRange = () =>
      Math.max(1, document.documentElement.scrollHeight - window.innerHeight);

    const update = () => {
      frame = 0;
      const scrollY = window.scrollY || 0;
      const progress = clamp01(scrollY / scrollRange());
      const thread = threadRef.current;
      if (thread) {
        thread.style.setProperty('--thread-progress', progress.toFixed(4));
        // Out of the way over the hero; it appears once reading starts.
        thread.dataset.awake = String(scrollY > window.innerHeight * 0.55);
      }
      setReached(stopsRef.current.filter((stop) => progress >= stop.at - 0.002).length);
    };

    const measure = () => {
      const range = scrollRange();
      const next = Array.from(document.querySelectorAll('[data-canto]')).map((section) => {
        const top = section.getBoundingClientRect().top + (window.scrollY || 0);
        return {
          id: section.id,
          numeral: section.getAttribute('data-canto'),
          title: section.getAttribute('data-canto-title'),
          // A stop lights up once its section is well into the viewport.
          at: clamp01((top - window.innerHeight * 0.45) / range),
        };
      });
      stopsRef.current = next;
      setStops(next);
      update();
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (resizeObserver) resizeObserver.observe(document.body);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [enabled, pathname]);

  if (!hydrated || !enabled) return null;

  const goTo = (id) => {
    const section = id && document.getElementById(id);
    if (!section) return;
    section.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  // Kept mounted (and measured) on pages without cantos, just not shown.
  return (
    <div className={`gold-thread ${stops.length ? '' : 'is-empty'}`} ref={threadRef}>
      <div className="gold-thread-track" aria-hidden="true">
        <div className="gold-thread-fill" />
        <div className="gold-thread-tip" />
      </div>
      {stops.length > 0 && (
        <nav className="gold-thread-stops" aria-label="Cantos on this page">
          {stops.map((stop, index) => (
            <button
              key={stop.id || stop.numeral}
              type="button"
              className={`gold-thread-stop ${index < reached ? 'is-reached' : ''}`}
              style={{ top: `${stop.at * 100}%` }}
              onClick={() => goTo(stop.id)}
              aria-label={`Canto ${stop.numeral}: ${stop.title}`}
            >
              <span className="gold-thread-stop-label" aria-hidden="true">
                <span className="gold-thread-stop-numeral">Canto {stop.numeral}</span>
                {stop.title}
              </span>
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
